import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Plus, Radio, Save, Settings2, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer';
import useIsMobile from '@/hooks/useIsMobile';
import { sprekersApi } from '../api';
import CanvasBoardSettingsPanel from '../canvas-editor/CanvasBoardSettingsPanel';
import CanvasEditorPreview from '../canvas-editor/CanvasEditorPreview';
import CanvasEditorStage from '../canvas-editor/CanvasEditorStage';
import CanvasFrameEditable from '../canvas-editor/CanvasFrameEditable';
import CanvasFrameInspector from '../canvas-editor/CanvasFrameInspector';
import CanvasSaveTemplateDialog from '../canvas-editor/CanvasSaveTemplateDialog';
import CanvasTemplatePickerDialog from '../canvas-editor/CanvasTemplatePickerDialog';
import type { CanvasBoardFieldPatch, CanvasFrameFieldPatch } from '../canvas-editor/canvasEditorTypes';
import type { CanvasBoardDetail, PresentationDetail } from '../types';

/**
 * Hoofdpagina van de Canvas Editor (Stap 2) -- vervangt het kale
 * "JSON importeren"-scherm van Stap 1 voor wie zelf, visueel, een canvas
 * wil opbouwen. Vol scherm zoals de live-schermen (geen SprekersLayout):
 * dit is een immersieve werkmodus, geen doorsnee beheerpagina.
 */
export default function CanvasEditorPage() {
  const { engagementId } = useParams<{ engagementId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [presentation, setPresentation] = useState<PresentationDetail | null>(null);
  const [board, setBoard] = useState<CanvasBoardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFrameId, setSelectedFrameId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  const boardRef = useRef<CanvasBoardDetail | null>(null);
  // Inspector fields autosave on blur. Queue those writes so two quick edits
  // cannot both use the same optimistic-lock revision.
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());

  const fetchBoard = useCallback(async (presentationId: string) => {
    try {
      const data = await sprekersApi.getCanvasBoard(presentationId);
      boardRef.current = data;
      setBoard(data);
    } catch (error) {
      setBoard(null);
    }
  }, []);

  useEffect(() => {
    if (!engagementId) return;
    (async () => {
      setLoading(true);
      try {
        const pres = await sprekersApi.getPresentation(engagementId);
        setPresentation(pres);
        if (pres.presentation_type === 'canvas') await fetchBoard(pres.id);
      } catch (error) {
        setPresentation(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [engagementId, fetchBoard]);

  const refetchBoard = () => { if (presentation) fetchBoard(presentation.id); };

  const selectedFrame = board?.frames.find((f) => f.id === selectedFrameId) || null;
  const otherFrames = board?.frames.filter((f) => f.id !== selectedFrameId) || [];

  // 400 komt van onze EIGEN validatie (bv. "Pad verwijst naar onbekend
  // frame-id") -- dat is zinvol om letterlijk te tonen. Andere statussen
  // (404, 401/403, 500, netwerkfouten) zijn tijdens autosave bijna nooit
  // door de gebruiker zelf veroorzaakt en de ruwe backend-tekst ("Spreekbeurt
  // niet gevonden") suggereert ten onrechte dat het werk kwijt is -- vandaar
  // een rustigere, generieke melding voor die gevallen.
  const describeSaveError = (error: any): string => {
    const status = error.response?.status;
    if (status === 400) return error.response?.data?.detail || 'Opslaan mislukt';
    if (status === 404) return 'Opslaan niet gelukt -- ververs de pagina en probeer het opnieuw.';
    return 'Opslaan tijdelijk niet gelukt. Probeer het nog eens.';
  };

  const handleFrameCommit = async (frameId: string, patch: CanvasFrameFieldPatch) => {
    const save = async () => {
      const currentBoard = boardRef.current;
      if (!currentBoard) return;
      const frame = currentBoard.frames.find((f) => f.id === frameId);
      if (!frame) return;
      try {
        const updated = await sprekersApi.updateCanvasFrame(currentBoard.presentation_id, frameId, { revision: frame.revision, ...patch });
        const nextBoard = boardRef.current
          ? { ...boardRef.current, frames: boardRef.current.frames.map((f) => (f.id === frameId ? updated : f)) }
          : null;
        boardRef.current = nextBoard;
        setBoard(nextBoard);
        toast.success('Opgeslagen', { duration: 1200 });
      } catch (error: any) {
        if (error.response?.status === 409) {
          toast.error('Dit frame is ondertussen gewijzigd -- het canvas wordt opnieuw geladen.');
          refetchBoard();
        } else {
          toast.error(describeSaveError(error));
        }
      }
    };
    saveQueueRef.current = saveQueueRef.current.then(save, save);
  };

  const handleBoardCommit = async (patch: CanvasBoardFieldPatch) => {
    const save = async () => {
      const currentBoard = boardRef.current;
      if (!currentBoard || !presentation) return;
      try {
        const updated = await sprekersApi.updateCanvasBoard(presentation.id, { revision: currentBoard.revision, ...patch });
        boardRef.current = updated;
        setBoard(updated);
        toast.success('Opgeslagen', { duration: 1200 });
      } catch (error: any) {
        if (error.response?.status === 409) {
          toast.error('Het canvas is ondertussen gewijzigd -- het wordt opnieuw geladen.');
          refetchBoard();
        } else {
          toast.error(describeSaveError(error));
        }
      }
    };
    saveQueueRef.current = saveQueueRef.current.then(save, save);
  };

  const handleAddFrame = async () => {
    if (!presentation) return;
    try {
      const frame = await sprekersApi.createCanvasFrame(presentation.id, {
        x: 200, y: 200, width: 600, height: 300, title: 'Nieuw frame',
      });
      setBoard((prev) => (prev ? { ...prev, frames: [...prev.frames, frame] } : prev));
      setSelectedFrameId(frame.id);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Frame toevoegen mislukt');
    }
  };

  const handleDeleteFrame = async () => {
    if (!selectedFrame) return;
    if (!window.confirm(`"${selectedFrame.title || selectedFrame.id}" verwijderen?`)) return;
    try {
      const updated = await sprekersApi.deleteCanvasFrame(selectedFrame.id);
      setBoard(updated);
      setSelectedFrameId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const handleCreateEmptyBoard = async () => {
    if (!presentation) return;
    try {
      const created = await sprekersApi.createEmptyCanvasBoard(presentation.id);
      setBoard(created);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Canvas aanmaken mislukt');
    }
  };

  const handleApplyTemplate = async (templateRef: string) => {
    if (!presentation) return;
    const updated = await sprekersApi.applyCanvasTemplate(presentation.id, templateRef);
    setBoard(updated);
    setSelectedFrameId(null);
  };

  const handlePublish = async () => {
    if (!presentation) return;
    setPublishing(true);
    try {
      await sprekersApi.updatePresentation(presentation.id, { status: 'gepubliceerd' });
      toast.success('Gepubliceerd');
      setPresentation({ ...presentation, status: 'gepubliceerd' });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Publiceren mislukt');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-400">Laden...</div>;
  }

  if (!presentation || presentation.presentation_type !== 'canvas') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 text-slate-300">
        <p>Geen canvas-presentatie gevonden.</p>
        <Button variant="outline" onClick={() => navigate(`/sprekers/${engagementId}`)}>Terug</Button>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-slate-950 text-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 p-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/sprekers/${engagementId}`)} aria-label="Terug">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-sm font-semibold">Canvas bewerken</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {board && (
            <>
              <Button variant="outline" size="sm" onClick={handleAddFrame} aria-label="Frame toevoegen">
                <Plus className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Frame</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setTemplatePickerOpen(true)} aria-label="Sjabloon toepassen">
                <Upload className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Sjabloon toepassen</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSaveTemplateOpen(true)} aria-label="Opslaan als sjabloon">
                <Save className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Opslaan als sjabloon</span>
              </Button>
              {isMobile && (
                <Button
                  variant={mobileSettingsOpen ? 'default' : 'outline'} size="sm" aria-label="Canvas-instellingen"
                  onClick={() => { setSelectedFrameId(null); setMobileSettingsOpen(true); }}
                >
                  <Settings2 className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                variant={previewOpen ? 'default' : 'outline'} size="sm" aria-label="Preview"
                onClick={() => setPreviewOpen((p) => !p)}
              >
                <Eye className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Preview</span>
              </Button>
            </>
          )}
          <Button size="sm" onClick={handlePublish} disabled={publishing || !board} aria-label="Publiceren">
            <Radio className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Publiceren</span>
          </Button>
        </div>
      </div>

      {!board ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <p className="text-sm text-slate-400">Nog geen canvas voor deze presentatie.</p>
          <div className="flex gap-2">
            <Button onClick={handleCreateEmptyBoard}>Leeg canvas aanmaken</Button>
            <Button variant="outline" onClick={() => setTemplatePickerOpen(true)}>Sjabloon toepassen</Button>
          </div>
        </div>
      ) : previewOpen ? (
        <div className="flex-1 p-4">
          <CanvasEditorPreview board={board} />
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1">
            <CanvasEditorStage
              board={{ width: board.width, height: board.height }}
              onBackgroundPointerDown={() => setSelectedFrameId(null)}
            >
              {board.frames.map((frame) => (
                <CanvasFrameEditable
                  key={frame.id}
                  frame={frame}
                  selected={frame.id === selectedFrameId}
                  onSelect={() => { setSelectedFrameId(frame.id); setMobileSettingsOpen(false); }}
                  onCommit={(patch) => handleFrameCommit(frame.id, patch)}
                />
              ))}
            </CanvasEditorStage>
          </div>
          {!isMobile && (
            <div className="w-80 shrink-0 overflow-y-auto border-l border-slate-800 bg-slate-900 p-4">
              {selectedFrame ? (
                <CanvasFrameInspector
                  frame={selectedFrame}
                  otherFrames={otherFrames}
                  onChange={(patch) => handleFrameCommit(selectedFrame.id, patch)}
                  onDelete={handleDeleteFrame}
                />
              ) : (
                <CanvasBoardSettingsPanel board={board} onChange={handleBoardCommit} />
              )}
            </div>
          )}
        </div>
      )}

      {isMobile && (
        <Drawer
          open={Boolean(selectedFrame) || mobileSettingsOpen}
          onOpenChange={(open: boolean) => { if (!open) { setSelectedFrameId(null); setMobileSettingsOpen(false); } }}
        >
          <DrawerContent className="max-h-[85vh] border-slate-800 bg-slate-900 text-white">
            <DrawerTitle className="sr-only">Canvas-editorpaneel</DrawerTitle>
            <DrawerDescription className="sr-only">
              {selectedFrame ? 'Eigenschappen van het geselecteerde frame.' : 'Instellingen van het canvas.'}
            </DrawerDescription>
            <Button
              variant="ghost" size="icon" aria-label="Sluiten" className="absolute right-3 top-3"
              onClick={() => { setSelectedFrameId(null); setMobileSettingsOpen(false); }}
            >
              <X className="h-4 w-4" />
            </Button>
            <div className="overflow-y-auto px-4 pb-6 pt-6">
              {board && selectedFrame ? (
                <CanvasFrameInspector
                  frame={selectedFrame}
                  otherFrames={otherFrames}
                  onChange={(patch) => handleFrameCommit(selectedFrame.id, patch)}
                  onDelete={handleDeleteFrame}
                />
              ) : board ? (
                <CanvasBoardSettingsPanel board={board} onChange={handleBoardCommit} />
              ) : null}
            </div>
          </DrawerContent>
        </Drawer>
      )}

      <CanvasTemplatePickerDialog open={templatePickerOpen} onOpenChange={setTemplatePickerOpen} onApply={handleApplyTemplate} />
      {board && (
        <CanvasSaveTemplateDialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen} presentationId={presentation.id} />
      )}
    </div>
  );
}
